import React, { useState, useEffect, useRef } from 'react';
import { usePageTitle } from '../hooks/usePageTitle.js';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore.js';
import { apiClient, ApiError, RETURN_PATH_KEY } from '../utils/apiClient.js';
import { safeRedirect } from '../utils/safeRedirect.js';
import { Button, Segmented, Field, Input, useToast } from '../components/ui/index.js';
import { ChevronRight, Shield, Eye, EyeOff } from 'lucide-react';

interface AuthResponse {
  token: string;
  user: {
    id: string;
    email: string;
    full_name: string;
    role: 'USER' | 'ADMIN';
    preferred_locale: string;
    preferred_theme: 'DARK' | 'LIGHT';
    email_verified?: boolean;
  };
}

/** FE-45 / FND-01: same-origin in-app paths only, fallback /dashboard (see utils/safeRedirect). */
const safeFrom = (from: unknown): string => safeRedirect(from, '/dashboard');

export const AuthPage: React.FC = () => {
  usePageTitle('ورود و ثبت‌نام');
  const navigate = useNavigate();
  const location = useLocation() as { state?: { from?: unknown } };
  const [searchParams] = useSearchParams();
  const toast = useToast();
  const { token, isAuthenticated, isGuest, setAuth, setGuestMode } = useAuthStore();

  const initialMode = searchParams.get('mode') === 'register' ? 'register' : 'login';
  const [mode, setMode] = useState<'login' | 'register'>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [fullNameError, setFullNameError] = useState<string | null>(null);
  const [fullNameTouched, setFullNameTouched] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  const segmentedRef = useRef<HTMLDivElement>(null);
  const savedReturnRef = useRef<string | null>(null);

  // FE-44: Check and clear password-changed logout reason
  // FND-09: also handles an expired session and remembers the path to return to after login.
  useEffect(() => {
    try {
      const reason = sessionStorage.getItem('fretboard_logout_reason');
      if (reason === 'password_changed') {
        sessionStorage.removeItem('fretboard_logout_reason');
        setErrorMessage('رمز عبور حساب تغییر کرده است؛ لطفاً دوباره وارد شوید.');
      } else if (reason === 'session_expired') {
        sessionStorage.removeItem('fretboard_logout_reason');
        setErrorMessage('نشست شما منقضی شده است؛ لطفاً دوباره وارد شوید.');
      }
      const saved = sessionStorage.getItem(RETURN_PATH_KEY);
      if (saved) {
        sessionStorage.removeItem(RETURN_PATH_KEY);
        savedReturnRef.current = safeRedirect(saved, '');
      }
    } catch {
      // ignore
    }
  }, []);

  /** Post-login target: router state, ?from=, then the path saved when the session expired. */
  const resolveTarget = (): string =>
    safeFrom(location.state?.from ?? searchParams.get('from') ?? (savedReturnRef.current || undefined));

  // AC-AUTH-4: If already authenticated (real user session, not guest), redirect to requested page or dashboard
  useEffect(() => {
    const storedToken = token || localStorage.getItem('fretboard_token');
    const storedGuest = isGuest || localStorage.getItem('fretboard_is_guest') === 'true';
    if (storedToken && !storedGuest) {
      const target = safeFrom(
        location.state?.from ?? searchParams.get('from') ?? (savedReturnRef.current || undefined)
      );
      navigate(target, { replace: true });
    }
  }, [isAuthenticated, isGuest, token, navigate, location.state, searchParams]);

  // Sync mode with query param if present
  useEffect(() => {
    const modeParam = searchParams.get('mode');
    if (modeParam === 'register' || modeParam === 'login') {
      setMode(modeParam);
      setErrorMessage(null);
      setPasswordError(null);
      setPasswordTouched(false);
      setFullNameError(null);
      setFullNameTouched(false);
      setEmailError(null);
    }
  }, [searchParams]);

  // Attach testid attributes to Segmented buttons
  useEffect(() => {
    if (!segmentedRef.current) return;
    const tabs = segmentedRef.current.querySelectorAll<HTMLButtonElement>('[role="tab"]');
    if (tabs[0]) tabs[0].setAttribute('data-testid', 'auth-segment-login');
    if (tabs[1]) tabs[1].setAttribute('data-testid', 'auth-segment-register');
  }, [mode]);

  const handleModeChange = (newMode: 'login' | 'register') => {
    setMode(newMode);
    setErrorMessage(null);
    setPasswordError(null);
    setPasswordTouched(false);
    setFullNameError(null);
    setFullNameTouched(false);
    setEmailError(null);
  };

  const validateFullName = (name: string): string | null => {
    const trimmed = name.trim();
    if (trimmed.length < 2) return 'نام باید حداقل ۲ حرف باشد.';
    if (trimmed.length > 100) return 'نام حداکثر ۱۰۰ حرف است.';
    return null;
  };

  const handleFullNameChange = (val: string) => {
    setFullName(val);
    if (mode === 'register' && (fullNameTouched || fullNameError)) {
      setFullNameError(validateFullName(val));
    }
  };

  const handleFullNameBlur = () => {
    setFullNameTouched(true);
    if (mode === 'register') {
      setFullNameError(validateFullName(fullName));
    }
  };

  const validateEmail = (val: string): string | null => {
    const trimmed = val.trim();
    if (!trimmed) {
      return 'لطفاً ایمیل خود را وارد کنید.';
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      return 'ایمیل وارد شده معتبر نیست.';
    }
    return null;
  };

  const validatePassword = (val: string, currentMode: 'login' | 'register'): string | null => {
    if (!val) {
      return 'لطفاً رمز عبور خود را وارد کنید.';
    }
    if (currentMode === 'register' && val.length < 8) {
      return 'رمز عبور باید حداقل ۸ کاراکتر باشد.';
    }
    return null;
  };

  const handlePasswordChange = (val: string) => {
    setPassword(val);
    if (passwordTouched || passwordError) {
      setPasswordError(validatePassword(val, mode));
    }
  };

  const handlePasswordBlur = () => {
    setPasswordTouched(true);
    if (passwordTouched || passwordError || mode === 'register') {
      setPasswordError(validatePassword(password, mode));
    }
  };

  const handleEmailChange = (val: string) => {
    setEmail(val);
    if (emailError) {
      setEmailError(validateEmail(val));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    const emailErr = validateEmail(email);
    const passErr = validatePassword(password, mode);
    let nameErr: string | null = null;
    if (mode === 'register') {
      nameErr = validateFullName(fullName);
    }

    setEmailError(emailErr);
    setPasswordError(passErr);
    if (mode === 'register') {
      setFullNameError(nameErr);
      if (nameErr) setFullNameTouched(true);
    }
    if (passErr) setPasswordTouched(true);

    if (emailErr || passErr || nameErr) {
      return;
    }

    setLoading(true);

    try {
      if (mode === 'login') {
        const response = await apiClient.post<AuthResponse>('/api/auth/login', {
          email: email.trim(),
          password
        });

        setAuth(
          {
            id: response.user.id,
            email: response.user.email,
            fullName: response.user.full_name,
            role: response.user.role,
            preferredTheme: response.user.preferred_theme,
            preferredLocale: response.user.preferred_locale,
            emailVerified: response.user.email_verified
          },
          response.token
        );
        toast.success('خوش آمدید!');
        const target = resolveTarget();
        navigate(target);
      } else {
        const response = await apiClient.post<AuthResponse>('/api/auth/register', {
          email: email.trim(),
          password,
          full_name: fullName.trim()
        });

        setAuth(
          {
            id: response.user.id,
            email: response.user.email,
            fullName: response.user.full_name,
            role: response.user.role,
            preferredTheme: response.user.preferred_theme,
            preferredLocale: response.user.preferred_locale,
            emailVerified: response.user.email_verified
          },
          response.token
        );
        toast.success('ثبت‌نام با موفقیت انجام شد!');
        if (response.user.email_verified === false) useAuthStore.getState().openVerifyDialog();
        const target = resolveTarget();
        navigate(target);
      }
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        if (err.status === 401) {
          setErrorMessage('ایمیل یا رمز عبور اشتباه است.');
        } else if (err.status === 409) {
          setErrorMessage('این ایمیل قبلاً در سیستم ثبت شده است.');
        } else if (err.status === 429) {
          setErrorMessage('تعداد تلاش‌های شما بیش از حد مجاز بوده است. لطفاً کمی صبر کنید و بعداً دوباره تلاش کنید.');
        } else if (err.status === 400) {
          let mappedField = false;
          const details = err.data?.details as Record<string, unknown> | undefined;
          if (details && typeof details === 'object') {
            if ('password' in details && details.password) {
              setPasswordError('رمز عبور باید حداقل ۸ کاراکتر باشد.');
              mappedField = true;
            }
            if (('full_name' in details && details.full_name) || ('fullName' in details && details.fullName)) {
              setFullNameError(fullName.trim().length > 100 ? 'نام حداکثر ۱۰۰ حرف است.' : 'نام باید حداقل ۲ حرف باشد.');
              mappedField = true;
            }
            if ('email' in details && details.email) {
              setEmailError('ایمیل معتبر نیست.');
              mappedField = true;
            }
          }
          if (!mappedField) {
            setErrorMessage('اطلاعات وارد شده معتبر نمی‌باشد.');
          }
        } else if (err.status >= 500) {
          setErrorMessage('خطایی در سرور رخ داد؛ لطفاً چند لحظه بعد دوباره تلاش کنید.');
        } else {
          setErrorMessage('خطایی در ارتباط با سرور رخ داد.');
        }
      } else {
        setErrorMessage('خطای شبکه یا ارتباط با سرور.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGuestMode = () => {
    setGuestMode();
    const target = resolveTarget();
    navigate(target);
  };

  if (isAuthenticated && !isGuest && token) {
    return null;
  }

  return (
    <div dir="rtl" className="min-h-full flex flex-col bg-base-100 text-base-content font-sans">
      {/* Public Header */}
      <header className="h-14 md:h-16 min-h-[56px] md:min-h-[64px] border-b border-transparent md:border-base-300 px-5 md:px-6 flex items-center justify-between bg-base-100 select-none">
        {/* On mobile: placed at end (left). On desktop: placed at start (right) */}
        <Link
          to="/"
          className="flex items-center gap-2.5 text-base-content hover:opacity-90 transition order-2 md:order-1"
        >
          <img
            src="/icon.svg"
            alt="FretBoard"
            className="w-8 h-8 rounded-[9px] object-cover"
          />
          <span className="hidden md:inline font-extrabold text-base tracking-wide" dir="ltr">
            FretBoard
          </span>
        </Link>

        {/* On mobile: back button at start (right). On desktop: text link at end (left) */}
        <Link
          to="/"
          aria-label="بازگشت به صفحه اصلی"
          className="flex items-center justify-center text-xs md:text-sm font-semibold text-base-content/70 hover:text-base-content transition order-1 md:order-2 -ms-2.5 md:ms-0 size-11 md:size-auto md:px-0 rounded-xl"
        >
          <ChevronRight className="md:hidden size-5" />
          <span className="hidden md:inline">بازگشت به صفحه اصلی</span>
          <span className="sr-only md:hidden">بازگشت به صفحه اصلی</span>
        </Link>
      </header>

      {/* Main Container */}
      <main className="flex-1 flex flex-col items-center justify-between md:justify-center px-5 py-4 md:py-12">
        <div
          data-testid="auth-card"
          className="w-full max-w-[420px] flex flex-col flex-1 md:flex-initial justify-between md:justify-start"
        >
          {/* Top Section: Title & Card Content */}
          <div className="flex flex-col flex-1 md:flex-initial">
            <div className="text-start md:text-center pt-2 pb-5 md:pt-0 md:pb-6">
              <h1 className="text-[26px] font-extrabold tracking-tight m-0 text-base-content">
                {mode === 'login' ? 'ورود به فرت‌بورد' : 'ساخت حساب'}
              </h1>
              <p className="mt-1.5 text-sm text-base-content/60 m-0">
                {mode === 'login'
                  ? 'برای همگام‌سازی تبلچرها و سوابق تمرین وارد شوید.'
                  : 'به جامعهٔ نوازندگان فرت‌بورد بپیوندید.'}
              </p>
            </div>

            {/* Card Surface */}
            <div className="md:bg-base-200 md:border md:border-base-300 md:rounded-[20px] md:p-6 flex flex-col gap-4 flex-1 md:flex-initial justify-between md:justify-start">
              <div className="flex flex-col gap-4">
                {/* Segmented Tabs */}
                <div ref={segmentedRef}>
                  <Segmented<'login' | 'register'>
                    fullWidth
                    size="lg"
                    value={mode}
                    onChange={handleModeChange}
                    className="bg-base-200 lg:bg-base-100"
                    options={[
                      { value: 'login', label: 'ورود' },
                      { value: 'register', label: 'ثبت‌نام' }
                    ]}
                  />
                </div>

                {/* Server Error Alert Banner */}
                {errorMessage && (
                  <div
                    data-testid="auth-error-alert"
                    role="alert"
                    className="p-3 rounded-xl bg-error/15 border border-error/30 text-error-soft text-xs flex items-center gap-2"
                  >
                    <Shield className="size-4 shrink-0" />
                    <span>{errorMessage}</span>
                  </div>
                )}

                {/* Auth Form Fields */}
                <form onSubmit={handleSubmit} id="auth-form" noValidate className="flex flex-col gap-4 mt-1 md:mt-0">
                  {mode === 'register' && (
                    <Field label="نام و نام خانوادگی" id="auth-name-input">
                      <Input
                        id="auth-name-input"
                        data-testid="auth-name-input"
                        type="text"
                        placeholder="علی احمدی"
                        autoComplete="name"
                        maxLength={100}
                        value={fullName}
                        error={Boolean(fullNameError)}
                        aria-invalid={Boolean(fullNameError)}
                        aria-describedby={fullNameError ? 'auth-name-error' : undefined}
                        onChange={(e) => handleFullNameChange(e.target.value)}
                        onBlur={handleFullNameBlur}
                        sizeVariant="lg"
                        className={`bg-base-200 md:bg-base-100 ${fullNameError ? 'border-error' : ''}`}
                        required
                      />
                      {fullNameError && (
                        <span
                          id="auth-name-error"
                          data-testid="auth-name-error"
                          role="alert"
                          className="text-xs text-error mt-1.5 text-start"
                        >
                          {fullNameError}
                        </span>
                      )}
                    </Field>
                  )}

                  <Field label="ایمیل" id="auth-email-input">
                    <Input
                      id="auth-email-input"
                      data-testid="auth-email-input"
                      type="email"
                      dir="ltr"
                      placeholder="you@example.com"
                      autoComplete={mode === 'login' ? 'username' : 'email'}
                      value={email}
                      error={Boolean(emailError)}
                      aria-invalid={Boolean(emailError)}
                      aria-describedby={emailError ? 'auth-email-error' : undefined}
                      onChange={(e) => handleEmailChange(e.target.value)}
                      sizeVariant="lg"
                      className={`bg-base-200 md:bg-base-100 text-right ${emailError ? 'border-error' : ''}`}
                      required
                    />
                    {emailError && (
                      <span
                        id="auth-email-error"
                        data-testid="auth-email-error"
                        role="alert"
                        className="text-xs text-error mt-1.5 text-start"
                      >
                        {emailError}
                      </span>
                    )}
                  </Field>

                  <div className="flex flex-col gap-1.5">
                    <div className="flex items-center justify-between text-[13px] font-medium text-base-content/80">
                      <label htmlFor="auth-password-input">رمز عبور</label>
                      {mode === 'login' && (
                        <button
                          type="button"
                          onClick={() => toast.info('بازیابی رمز عبور به‌زودی فعال می‌شود.')}
                          className="min-h-[44px] inline-flex items-center px-2 -my-2.5 text-xs text-primary-soft hover:text-base-content transition focus:outline-none cursor-pointer"
                        >
                          فراموش کرده‌اید؟
                        </button>
                      )}
                    </div>
                    <div className="relative flex items-center">
                      <Input
                        id="auth-password-input"
                        data-testid="auth-password-input"
                        type={showPassword ? 'text' : 'password'}
                        dir="ltr"
                        autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                        maxLength={128}
                        error={Boolean(passwordError)}
                        aria-invalid={Boolean(passwordError)}
                        aria-describedby={passwordError ? 'auth-password-error' : undefined}
                        value={password}
                        onChange={(e) => handlePasswordChange(e.target.value)}
                        onBlur={handlePasswordBlur}
                        sizeVariant="lg"
                        className={`bg-base-200 md:bg-base-100 text-right pe-12 ${passwordError ? 'border-error' : ''}`}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((prev) => !prev)}
                        aria-label={showPassword ? 'پنهان کردن رمز عبور' : 'نمایش رمز عبور'}
                        title={showPassword ? 'پنهان کردن رمز عبور' : 'نمایش رمز عبور'}
                        data-testid="auth-toggle-password-visibility"
                        className="absolute end-0 top-0 bottom-0 min-w-[44px] min-h-[44px] px-3 flex items-center justify-center text-base-content/50 hover:text-base-content transition cursor-pointer"
                      >
                        {showPassword ? (
                          <EyeOff className="size-5" />
                        ) : (
                          <Eye className="size-5" />
                        )}
                      </button>
                    </div>
                    {passwordError && (
                      <span
                        id="auth-password-error"
                        data-testid="auth-password-error"
                        role="alert"
                        className="text-xs text-error mt-1.5 text-start"
                      >
                        {passwordError}
                      </span>
                    )}
                  </div>
                </form>
              </div>

              {/* Mobile spacer to push buttons down */}
              <div className="flex-1 md:hidden min-h-8" />

              {/* Action Buttons */}
              <div className="flex flex-col gap-2.5 md:gap-4 mt-2 md:mt-0 pb-6 md:pb-0">
                <Button
                  data-testid="auth-submit-button"
                  form="auth-form"
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  loading={loading}
                  className="h-[52px] md:h-12 rounded-[14px] md:rounded-xl md:!text-[15px] font-semibold text-white"
                >
                  {mode === 'login' ? 'ورود' : 'ساخت حساب'}
                </Button>

                <div className="hidden md:flex items-center gap-3 text-xs text-base-content/40 my-0.5">
                  <span className="flex-1 h-px bg-base-300" />
                  <span>یا</span>
                  <span className="flex-1 h-px bg-base-300" />
                </div>

                <Button
                  data-testid="auth-guest-button"
                  type="button"
                  variant="secondary"
                  size="lg"
                  fullWidth
                  onClick={handleGuestMode}
                  className="h-[52px] md:h-12 rounded-[14px] md:rounded-xl md:!text-[15px] font-semibold"
                >
                  <span className="hidden">ادامه به عنوان مهمان</span>
                  <span>ادامه به‌عنوان مهمان</span>
                </Button>

                <p className="hidden md:block text-xs text-center text-base-content/60 leading-relaxed m-0">
                  در حالت مهمان همه‌چیز روی همین دستگاه ذخیره می‌شود و بعداً می‌توانید حساب بسازید.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AuthPage;
